/**
 * 予測誤差評価 APIクライアント
 */

import { getAppConfig } from '../../config';
import type {
  StartPredictionErrorEvaluationRequest,
  StartPredictionErrorEvaluationResponse,
  PredictionErrorEvaluationJob,
  PredictionErrorEvaluationDownloadResponse,
  InstanceQuotasResponse,
} from '@/lib/types/ds/prediction-error-evaluation';

function buildAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }
  if (baseUrl.startsWith('/')) {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
    const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
    const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
    return `${origin}${base}${pathWithSlash}`;
  }
  const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const pathWithSlash = path.startsWith('/') ? path : `/${path}`;
  return `${base}${pathWithSlash}`;
}

function getHeaders(authHeaders?: Record<string, string>): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...authHeaders,
  };
}

function getBaseUrl(): string {
  const config = getAppConfig();
  if (!config.apiBaseUrl) {
    throw new Error(
      `API Base URLが設定されていません。環境設定を確認してください。現在の環境: ${config.env}`,
    );
  }
  return config.apiBaseUrl;
}

/** ジョブ起動 */
export async function startPredictionErrorEvaluation(
  accountCode: string,
  request: StartPredictionErrorEvaluationRequest,
  authHeaders?: Record<string, string>,
): Promise<StartPredictionErrorEvaluationResponse> {
  const url = new URL(
    buildAbsoluteUrl(getBaseUrl(), `/${accountCode}/ds/prediction-error-evaluation/start`),
  );

  const response = await fetch(url.toString(), {
    method: 'POST',
    headers: getHeaders(authHeaders),
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    let message = `予測誤差評価ジョブの起動に失敗しました: ${response.status}`;
    try {
      const parsed = JSON.parse(errorBody);
      if (parsed.error) {
        message = parsed.error;
      }
    } catch {
      // JSONパース失敗時はデフォルトメッセージを使用
    }
    throw new Error(message);
  }

  return response.json();
}

/** ジョブ詳細取得 */
export async function getPredictionErrorEvaluationJob(
  accountCode: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<PredictionErrorEvaluationJob> {
  const url = new URL(
    buildAbsoluteUrl(getBaseUrl(), `/${accountCode}/ds/prediction-error-evaluation/jobs/${jobId}`),
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    throw new Error(`予測誤差評価ジョブ詳細の取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/** ジョブ一覧取得 */
export async function listPredictionErrorEvaluationJobs(
  accountCode: string,
  authHeaders?: Record<string, string>,
  limit?: number,
): Promise<PredictionErrorEvaluationJob[]> {
  const params = new URLSearchParams();
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }
  const queryStr = params.toString() ? `?${params.toString()}` : '';

  const url = new URL(
    buildAbsoluteUrl(
      getBaseUrl(),
      `/${accountCode}/ds/prediction-error-evaluation/jobs${queryStr}`,
    ),
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    throw new Error(`予測誤差評価ジョブ一覧の取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/** 全アカウントのジョブ一覧取得 */
export async function listAllPredictionErrorEvaluationJobs(
  authHeaders?: Record<string, string>,
  limit?: number,
  executedBy?: string,
): Promise<PredictionErrorEvaluationJob[]> {
  const params = new URLSearchParams();
  if (limit !== undefined) {
    params.append('limit', limit.toString());
  }
  if (executedBy) {
    params.append('executed_by', executedBy);
  }
  const queryStr = params.toString() ? `?${params.toString()}` : '';

  const url = new URL(
    buildAbsoluteUrl(getBaseUrl(), `/ds/prediction-error-evaluation/all-jobs${queryStr}`),
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    throw new Error(`全ジョブ一覧の取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/** 結果ダウンロードURL取得 */
export async function getPredictionErrorEvaluationDownloadUrl(
  accountCode: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<PredictionErrorEvaluationDownloadResponse> {
  const url = new URL(
    buildAbsoluteUrl(
      getBaseUrl(),
      `/${accountCode}/ds/prediction-error-evaluation/jobs/${jobId}/download`,
    ),
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    throw new Error(`ダウンロードURL取得に失敗しました: ${response.status}`);
  }

  return response.json();
}

/** ジョブ削除 */
export async function deletePredictionErrorEvaluationJob(
  accountCode: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<{ message: string }> {
  const url = new URL(
    buildAbsoluteUrl(getBaseUrl(), `/${accountCode}/ds/prediction-error-evaluation/jobs/${jobId}`),
  );

  const response = await fetch(url.toString(), {
    method: 'DELETE',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error || `削除リクエストが失敗しました (${response.status})`;
    throw new Error(errorMessage);
  }

  return response.json();
}

/** インスタンスクォータ一括取得 */
export async function getInstanceQuotas(
  authHeaders?: Record<string, string>,
): Promise<InstanceQuotasResponse> {
  const url = new URL(
    buildAbsoluteUrl(getBaseUrl(), `/ds/prediction-error-evaluation/instance-quotas`),
  );

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: getHeaders(authHeaders),
  });

  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`クォータ取得に失敗しました: ${response.status} ${errorBody}`);
  }

  return response.json();
}

/** 結果ファイルをダウンロード */
export async function downloadPredictionErrorEvaluationResult(
  accountCode: string,
  jobId: string,
  authHeaders?: Record<string, string>,
): Promise<void> {
  const downloadInfo = await getPredictionErrorEvaluationDownloadUrl(
    accountCode,
    jobId,
    authHeaders,
  );
  const link = document.createElement('a');
  link.href = downloadInfo.download_url;
  link.download = downloadInfo.file_name;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
